import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type {
  ObjectiveDetailVM,
  TrainingItemAppraisalVM,
  TrainingItemEvaluationPayload,
} from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { sortObjectivesByNumber } from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Form } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { LongTextField } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { ObjectiveRow } from "./objective-row.content";
import type { FormInputs } from "./form-validation/training-item-evaluation.schema";
import { schema, buildFormValues, toEvaluationPayload } from "./form-validation/training-item-evaluation.schema";

interface Props {
  objectives: ObjectiveDetailVM[];
  appraisal: Nullable<TrainingItemAppraisalVM>;
  onSubmit: (payload: TrainingItemEvaluationPayload) => Promise<void>;
  onSubmitted?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

// L'évaluation se rend d'un seul geste : tous les objectifs du groupe notés, puis l'appréciation qui
// porte sur la formation menée. Un seul formulaire, donc — le backend rejette un envoi partiel.
// Conseiller et partenaire externe rendent le même acte ; seuls l'habillage et l'endpoint diffèrent.
export const TrainingItemEvaluationForm = ({ objectives, appraisal, onSubmit, onSubmitted, onDirtyChange }: Props) => {
  const sortedObjectives = useMemo(() => sortObjectivesByNumber(objectives), [objectives]);

  const form = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: buildFormValues(objectives, appraisal),
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = form;

  const ratings = watch("objectives");
  const unratedCount = ratings.filter(rating => !rating.acquisitionStatus).length;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Une re-lecture des données ne doit pas emporter une saisie en cours : sur cet écran plusieurs
  // formulaires partagent le même rafraîchissement, et en rendre un effacerait les autres. La saisie
  // se consulte par référence — elle décide du saut, elle ne doit pas déclencher de reset.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (isDirtyRef.current) return;
    reset(buildFormValues(objectives, appraisal));
  }, [objectives, appraisal, reset]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleOnSubmit = async (formBody: FormInputs) => {
    setIsSubmitting(true);
    try {
      await onSubmit(toEvaluationPayload(formBody));
      // Ce qui vient d'être rendu devient l'état de référence : le formulaire cesse d'être sale, et
      // la relecture qui suit pourra le repeupler.
      reset(formBody);
      onSubmitted?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4">
        {sortedObjectives.map((objective, index) => (
          <ObjectiveRow
            key={objective.id}
            objective={objective}
            index={index}
            selectedStatus={ratings[index]?.acquisitionStatus}
            onSelectStatus={status =>
              setValue(`objectives.${index}.acquisitionStatus`, status, { shouldDirty: true, shouldValidate: true })
            }
          />
        ))}

        <LongTextField
          control={control}
          name="disclosedAppraisal"
          rows={3}
          placeholder="Appréciation sur la formation, visible par l'élève..."
        />

        <LongTextField control={control} name="privateAppraisal" rows={3} placeholder="Appréciation privée (non visible par l'élève)..." />

        <Button type="submit" disabled={isSubmitting || unratedCount > 0} className="w-full">
          {isSubmitting
            ? "Enregistrement..."
            : unratedCount > 0
              ? "Évaluez chaque objectif"
              : appraisal
                ? "Mettre à jour l'évaluation"
                : "Enregistrer l'évaluation"}
        </Button>
      </form>
    </Form>
  );
};
